import {gql} from '@apollo/client';
import {Kind, parse, print} from 'graphql';
import {
  FRAGMENT_OWNER_DOCS_KEY,
  INLINE_FRAGMENT_REFS_KEY,
  attachHiddenDocumentMetadata,
  getFragmentOwnerDocs,
  getInlineFragmentRefs,
} from 'src/internal/shared/utils/hiddenDocumentMetadata';

import type {
  DocumentNode,
  FragmentDefinitionNode,
  InlineFragmentNode,
  SelectionNode,
} from 'graphql';

// Matches Apollo's graphql-tag normalization for fragment source deduplication.
function normalizeSource(source: string): string {
  return source.replace(/[\s,]+/g, ' ').trim();
}

function fragmentSourceKey(def: FragmentDefinitionNode): string {
  if (def.loc) {
    return normalizeSource(
      def.loc.source.body.substring(def.loc.start, def.loc.end),
    );
  }
  return def.name.value;
}

function isDocumentNode(value: unknown): value is DocumentNode {
  return (
    value !== null &&
    typeof value === 'object' &&
    'kind' in value &&
    'definitions' in value
  );
}

function getFragmentDefinitions(
  doc: DocumentNode,
): ReadonlyArray<FragmentDefinitionNode> {
  return doc.definitions.filter(
    (def): def is FragmentDefinitionNode =>
      def.kind === Kind.FRAGMENT_DEFINITION,
  );
}

function describeDocument(doc: DocumentNode): string {
  const firstDefinition = doc.definitions[0];

  if (!firstDefinition) {
    return 'an empty document';
  }

  if ('name' in firstDefinition && firstDefinition.name?.value) {
    return `${firstDefinition.kind} "${firstDefinition.name.value}"`;
  }

  return firstDefinition.kind;
}

function getPrimaryFragmentDefinition(
  doc: DocumentNode,
): FragmentDefinitionNode {
  const fragmentDefinition = getFragmentDefinitions(doc)[0];

  if (!fragmentDefinition) {
    throw new Error(
      `Inline document interpolation requires a fragment definition, but received ${describeDocument(
        doc,
      )}.`,
    );
  }

  return fragmentDefinition;
}

function getFragmentDefinitionByName(
  doc: DocumentNode,
  name: string,
): FragmentDefinitionNode | undefined {
  return doc.definitions.find(
    (def): def is FragmentDefinitionNode =>
      def.kind === Kind.FRAGMENT_DEFINITION && def.name.value === name,
  );
}

function getInlineFragmentRefKey(index: number): string {
  return `__INLINE_REF_${index}__`;
}

function registerFragmentOwner(
  ownerDocs: Record<string, DocumentNode>,
  sourceKeys: Map<string, string>,
  name: string,
  sourceKey: string,
  ownerDoc: DocumentNode,
): void {
  const existingKey = sourceKeys.get(name);

  if (existingKey != null) {
    if (existingKey !== sourceKey) {
      // eslint-disable-next-line no-console
      console.warn(`Warning: fragment with name ${name} already exists.\n`);
    }

    return;
  }

  sourceKeys.set(name, sourceKey);
  ownerDocs[name] = ownerDoc;
}

function registerFragmentOwnersForDocument(
  ownerDocs: Record<string, DocumentNode>,
  sourceKeys: Map<string, string>,
  doc: DocumentNode,
): void {
  // graphql-tag flattens fragment definitions into a single document, so we keep
  // track of which original document owned each fragment to recover nested
  // inline-fragment metadata later in RestLink.
  Object.entries(getFragmentOwnerDocs(doc)).forEach(([name, ownerDoc]) => {
    const fragmentDefinition = getFragmentDefinitionByName(ownerDoc, name);

    registerFragmentOwner(
      ownerDocs,
      sourceKeys,
      name,
      fragmentDefinition ? fragmentSourceKey(fragmentDefinition) : name,
      ownerDoc,
    );
  });

  getFragmentDefinitions(doc).forEach((fragmentDefinition) => {
    registerFragmentOwner(
      ownerDocs,
      sourceKeys,
      fragmentDefinition.name.value,
      fragmentSourceKey(fragmentDefinition),
      doc,
    );
  });
}

function attachMetadata(
  doc: DocumentNode,
  inlineFragmentRefs: Record<string, DocumentNode>,
  fragmentOwnerDocs: Record<string, DocumentNode>,
): DocumentNode {
  // Apollo can hand back the same cached DocumentNode for repeated gql calls, so
  // merge and redefine these hidden properties instead of assuming a fresh object.
  const nextInlineFragmentRefs = {
    ...getInlineFragmentRefs(doc),
    ...inlineFragmentRefs,
  };
  const nextFragmentOwnerDocs = {
    ...getFragmentOwnerDocs(doc),
    ...fragmentOwnerDocs,
  };

  if (
    Object.keys(nextInlineFragmentRefs).length === 0 &&
    Object.keys(nextFragmentOwnerDocs).length === 0
  ) {
    return doc;
  }

  return attachHiddenDocumentMetadata(doc, {
    inlineFragmentRefs: Object.freeze(nextInlineFragmentRefs),
    fragmentOwnerDocs: Object.freeze(nextFragmentOwnerDocs),
  });
}

// Walks the selection tree and replaces placeholder FragmentSpread nodes
// (e.g. `...__INLINE_REF_0__`) with InlineFragmentNodes built
// from the child document's first fragment definition. This preserves
// directives like @argumentDefinitions on the resulting inline fragment.
// If the child document contains additional fragment definitions (e.g. a
// shared data fragment), those are collected into auxiliaryDefs so they can
// be appended to the parent document.
function replaceSpreadSelections(
  selections: readonly SelectionNode[],
  inlineMap: Map<string, DocumentNode>,
  auxiliaryDefs: FragmentDefinitionNode[],
): SelectionNode[] {
  return selections.map((selection) => {
    if (
      selection.kind === Kind.FRAGMENT_SPREAD &&
      inlineMap.has(selection.name.value)
    ) {
      const childDoc = inlineMap.get(selection.name.value)!;
      const mainFrag = getPrimaryFragmentDefinition(childDoc);

      for (const def of childDoc.definitions) {
        if (def.kind === Kind.FRAGMENT_DEFINITION && def !== mainFrag) {
          auxiliaryDefs.push(def);
        }
      }

      const inlineFragment: InlineFragmentNode = {
        kind: Kind.INLINE_FRAGMENT,
        typeCondition: mainFrag.typeCondition,
        directives: mainFrag.directives ?? [],
        selectionSet: mainFrag.selectionSet,
      };

      return inlineFragment;
    }

    // Recurse into fields and inline fragments that have nested selections
    if ('selectionSet' in selection && selection.selectionSet) {
      return {
        ...selection,
        selectionSet: {
          ...selection.selectionSet,
          selections: replaceSpreadSelections(
            selection.selectionSet.selections,
            inlineMap,
            auxiliaryDefs,
          ),
        },
      };
    }

    return selection;
  });
}

// Returns a lazy DocumentNode that defers compilation until a property is
// first accessed. This avoids parsing overhead at module load time — the
// gql call only happens when the document is actually used.
export default function tag(...args: Parameters<typeof gql>): DocumentNode {
  let compiled: DocumentNode | null = null;

  const [strings, ...expressions] = args as [
    TemplateStringsArray,
    ...unknown[],
  ];

  const getCompiled = (): DocumentNode => {
    if (!compiled) {
      compiled = compile(strings, expressions);
    }

    return compiled;
  };

  const lazyDocument = {
    get kind() {
      return getCompiled().kind;
    },
    get definitions() {
      return getCompiled().definitions;
    },
    get loc() {
      return getCompiled().loc;
    },
  } as DocumentNode;

  // Expose the hidden metadata on the lazy wrapper too so callers see the same
  // document shape before the underlying gql call has been forced.
  Object.defineProperties(lazyDocument, {
    [INLINE_FRAGMENT_REFS_KEY]: {
      get() {
        return getInlineFragmentRefs(getCompiled());
      },
      enumerable: false,
    },
    [FRAGMENT_OWNER_DOCS_KEY]: {
      get() {
        return getFragmentOwnerDocs(getCompiled());
      },
      enumerable: false,
    },
  });

  return lazyDocument;
}

// Apollo's gql concatenates a DocumentNode interpolation's source text directly,
// so `...${documentNode}` would produce `...fragment Foo on Type { ... }` which
// is invalid GraphQL. To support `...${doc}` syntax, we replace DocumentNode
// interpolations preceded by `...` with unique placeholder strings. Apollo then
// parses `...__INLINE_REF_N__` as valid FragmentSpread nodes, which we
// swap in the post-processing step with InlineFragmentNodes built from the
// child document's fragment definition.
function compile(
  strings: TemplateStringsArray,
  expressions: unknown[],
): DocumentNode {
  const inlineMap = new Map<string, DocumentNode>();
  const inlineFragmentRefs: Record<string, DocumentNode> = {};
  const fragmentOwnerDocs: Record<string, DocumentNode> = {};
  const fragmentOwnerSourceKeys = new Map<string, string>();

  expressions.forEach((expr, index) => {
    if (!isDocumentNode(expr)) {
      return;
    }

    registerFragmentOwnersForDocument(
      fragmentOwnerDocs,
      fragmentOwnerSourceKeys,
      expr,
    );

    if (strings[index].trimEnd().endsWith('...')) {
      const placeholder = getInlineFragmentRefKey(index);
      getPrimaryFragmentDefinition(expr);
      inlineMap.set(placeholder, expr);
      inlineFragmentRefs[placeholder] = expr;
    }
  });

  const processedExpressions = expressions.map((expr, index) => {
    if (strings[index].trimEnd().endsWith('...') && isDocumentNode(expr)) {
      return getInlineFragmentRefKey(index);
    }

    return expr;
  });

  const rawDoc = gql(strings, ...processedExpressions);

  if (inlineMap.size === 0) {
    return attachMetadata(rawDoc, inlineFragmentRefs, fragmentOwnerDocs);
  }

  const auxiliaryDefs: FragmentDefinitionNode[] = [];

  const newDefinitions = rawDoc.definitions.map((def) => {
    if (!('selectionSet' in def) || !def.selectionSet) {
      return def;
    }

    return {
      ...def,
      selectionSet: {
        ...def.selectionSet,
        selections: replaceSpreadSelections(
          def.selectionSet.selections,
          inlineMap,
          auxiliaryDefs,
        ),
      },
    };
  });

  // Deduplicate auxiliary fragments following Apollo's graphql-tag approach:
  // dedup by normalized source content, warn on name collisions with
  // different source text.
  const seenKeys = new Set<string>();
  const nameToKey = new Map<string, string>();

  for (const def of newDefinitions) {
    if (def.kind === Kind.FRAGMENT_DEFINITION) {
      const key = fragmentSourceKey(def as FragmentDefinitionNode);
      seenKeys.add(key);
      nameToKey.set((def as FragmentDefinitionNode).name.value, key);
    }
  }

  const dedupedAux = auxiliaryDefs.filter((def) => {
    const name = def.name.value;
    const key = fragmentSourceKey(def);

    const existingKey = nameToKey.get(name);

    if (existingKey != null && existingKey !== key) {
      // eslint-disable-next-line no-console
      console.warn(`Warning: fragment with name ${name} already exists.\n`);
    }

    if (seenKeys.has(key)) {
      return false;
    }

    seenKeys.add(key);
    nameToKey.set(name, key);

    return true;
  });

  return attachMetadata(
    parse(
      print({
        ...rawDoc,
        definitions: [...newDefinitions, ...dedupedAux],
      }),
    ),
    inlineFragmentRefs,
    fragmentOwnerDocs,
  );
}
